import { Switch, Route, useRouteMatch, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { verifyHideCountryItems } from '../../utils/Common';
// provider
import ModalProvider from "../../contexts/modal/Provider";
// contexts
import AuthContext from '../../contexts/auth';
import SidebarContext from "../../contexts/sidebar";
// components
import Spinner from '../../components/Spinner';
import { ToastContainer, useToast } from '../../components/common/Toast';
import UserProfile from './UserProfile';
import Home from './Home';
import Clients from './Clients';
import Companies from './Companies';
import CompanyDetail from './Companies/CompanyDetail';
import Reservations from './Reservations';
import Faqs from './Faqs';
import Packages from './Packages';
import PackageDetailStandalone from './Packages/PackageDetailStandalone';
import AwaitingDelivery from './AwaitingDelivery';
import Insights from './Insights';
import PickLocation from './PickLocation';
import Delivery from './Delivery';
import './index.css';
import General from './Platforms';
import Support from './Platforms/Support';
import GenerateQR from './Platforms/GenerateQR';
import { Accounting } from './Accounting';
import Arching from './Accounting/Arching';
import Billing from './Billing';
import BillingAdmin from './Accounting/BillingAdmin';
import RegisterIncdent from './Incidents/Register';
import ListIncidents from './Incidents/List';
import UpdateIncident from './Incidents/UpdateIncident';
import IncidentReport from "./Incidents/Report";
import { createPathAccessChecker, verifyAccessViewPermissions } from '../../config/permissions';
import DetailsClients from './Clients/DetailsClients';
import CreateClient from './Clients/CreateClient';
import LocatePlatforms from './Platforms/LocatePlatforms';
import CreatePlatform from './Platforms/CreatePlatform';
import BottomNavigation from '../../components/BottomNavigation';
import MobileTopNavbar from '../../components/layout/MobileTopNavbar';
import ConfigurationPermissions from './Configuration';
import ConfigurationExceptions from './Configuration/Exceptions';
import Forbidden403 from './Forbidden403';

function stripTrailingSlash(p) {
    if (!p) return '';
    return p.length > 1 ? p.replace(/\/$/, '') : p;
}

const Dashboard = () => {
    const userContext = useContext(AuthContext);
    const sidebarContext = useContext(SidebarContext);
    let { url } = useRouteMatch();
    const history = useHistory();
    const location = useLocation();
    const [update, setUpdate] = useState(false);
    const { toasts, showError, removeToast, showInfo } = useToast();
    const lastBliErrorRef = useRef(null);

    const canAccessPath = useMemo(() => createPathAccessChecker(), []);

    useEffect(() => {
        const err = userContext?.bliPermissionsError;
        if (!err) {
            lastBliErrorRef.current = null;
            return;
        }
        if (lastBliErrorRef.current === err) return;
        lastBliErrorRef.current = err;

        showError(
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                    No se pudieron cargar permisos. Reintenta.
                </div>
                <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-muted text-foreground hover:bg-accent h-8 rounded-md px-3 text-xs"
                    onClick={async () => {
                        showInfo('Reintentando permisos...', 2500);
                        await userContext?.retryLoadBliPermissions?.();
                    }}
                >
                    Reintentar
                </button>
            </div>,
            0
        );
    }, [userContext?.bliPermissionsError, userContext?.retryLoadBliPermissions, showError, showInfo]);

    useEffect(() => {
        if (!userContext.user) return;
        const pathname = location.pathname;
        const base = stripTrailingSlash(url);
        const normPath = stripTrailingSlash(pathname);
        if (normPath === base) return;
        if (normPath === `${base}/profile`) return;
        if (normPath === `${base}/403`) return;
        if (!canAccessPath(pathname)) {
            history.replace(`${url}/403`);
        }
    }, [location.pathname, canAccessPath, userContext.user, url, history]);

    const updateSidebarInfo = () => setUpdate(!update);

    if (!sessionStorage.getItem('token') && userContext.loading) {
        return <div className="loader center">
            <Spinner type={0} size={40} />
        </div>
    }
    if (!userContext.loading && userContext.user === null) {
        history.push('/login');
        return null; // Evitar renderizar mientras redirige
    }

    return (
        <>
            <MobileTopNavbar />
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            <div
                id='mainLayout'
                className={"main-layout" + (sidebarContext.sidebarOpen ? "-open" : "")}
                onClick={() => (sidebarContext.isMobile && sidebarContext.sidebarOpen) ? sidebarContext.closeSidebar() : null} >
                <ModalProvider>
                    <Switch>
                        <Route path={`${url}`} component={Home} exact />
                        <Route path={`${url}/403`} component={Forbidden403} exact />
                        <Route path={`${url}/packages/tracking/package/:packageId`} component={PackageDetailStandalone} exact />
                        <Route path={`${url}/packages/:page`} component={Packages} />
                        <Route path={`${url}/lockers/reservations`} component={Reservations} exact />
                        <Route path={`${url}/lockers/awaitingDelivery`} component={AwaitingDelivery} exact />
                        <Route path={`${url}/addDelivery/package=:idPackage&from=:from&locName=:locName&redirect=:redirect`} component={PickLocation} exact />
                        <Route path={`${url}/accounting`} component={Accounting} exact />
                        <Route path={`${url}/faqs`} component={Faqs} exact />
                        <Route path={`${url}/billing`} component={Billing} exact />
                        <Route path={`${url}/incidents/register`} component={RegisterIncdent} exact />
                        <Route path={`${url}/incidents`} component={ListIncidents} exact />
                        <Route path={`${url}/incidents/report`} component={IncidentReport} exact />
                        <Route path={`${url}/incidents/:id`} component={UpdateIncident} exact />

                        <Route path={`${url}/companies`} component={Companies} exact />
                        <Route path={`${url}/companies/:companyId`} component={CompanyDetail} exact />

                        <Route path={`${url}/adminBilling`} component={BillingAdmin} exact />

                        <Route
                            path={`${url}/profile`}
                            exact
                            render={() => <UserProfile updateSidebarInfo={updateSidebarInfo} />}
                        />

                        <Route path={`${url}/insights`} component={Insights} exact />

                        <Route path={`${url}/deliveries`} exact>
                            {userContext.user?.IsAdmin ?
                                <Delivery />
                                :
                                <Redirect from={`${url}/deliveries`} to={`${url}/`} />
                            }
                        </Route>

                        <Route path={`${url}/platforms/support`} component={Support} exact />
                        <Route path={`${url}/platforms/locate`} component={LocatePlatforms} exact />
                        <Route path={`${url}/platforms/general`} component={General} exact />
                        <Route path={`${url}/platforms/GenerateQR`} component={GenerateQR} exact />

                        <Route path={`${url}/platforms/settings`} exact>
                            {(verifyHideCountryItems(["pa"]) && (userContext.user.roleId >= 4 || userContext.user.RoleBli >= 4)) ? (
                                <CreatePlatform />
                            ) : (
                                <Redirect from={`${url}/platforms/settings`} to={`${url}/`} />
                            )}
                        </Route>


                        <Route path={`${url}/clients`} component={Clients} exact />

                        <Route
                            path={`${url}/clients/details/:clientId`}
                            exact
                            render={() =>
                                userContext?.user &&
                                (userContext?.user?.IdTipoUsuario === 3 || userContext.user.IsAdmin) ? (
                                    <DetailsClients />
                                ) : (
                                    <Redirect to={`${url}/`} />
                                )
                            }
                        />

                        <Route path={`${url}/clients/create`} exact >
                            {userContext?.user?.IdTipoUsuario === 3 ?
                                <CreateClient />
                                :
                                <Redirect from={`${url}/clients/create`} to={`${url}/`} />
                            }
                        </Route>

                        <Route path={`${url}/accounting/arching`} component={Arching} exact />
                        <Route path={`${url}/packages/alerted`} component={Arching} exact />

                        <Route path={`${url}/settings`} exact>
                            {verifyAccessViewPermissions(['read'], { path: location.pathname }) ? (
                                <ConfigurationPermissions />
                            ) : (
                                <Redirect from={`${url}/settings`} to={`${url}/`} />
                            )}
                        </Route>

                        {/* Excepciones (ConfExceptions): resuelve al mismo módulo BLI que /settings */}
                        <Route path={`${url}/settings/exceptions`} exact>
                            {verifyAccessViewPermissions(['read'], { path: location.pathname }) ? (
                                <ConfigurationExceptions />
                            ) : (
                                <Redirect from={`${url}/settings/exceptions`} to={`${url}/`} />
                            )}
                        </Route>
                        <Redirect from={`${url}/*`} to={`${url}/`} />
                    </Switch>
                </ModalProvider>
            </div>
            <BottomNavigation />
        </>
    );
};

export default Dashboard;
